/**
 * US-134 / US-135 tier-billing UI (T-133-001/002/003, T-134-001/002/003,
 * T-135-001/002). Embedded in account settings (Settings > Subscription) for
 * the Texas (USD) market.
 *
 * Data comes entirely from existing backend endpoints via usdBillingApi:
 *   GET /api/usd-billing/meter|estimate|plan|plan/compare, POST /plan/upgrade.
 * The mid-cycle upgrade (T-134-002) calls POST /plan/upgrade and then
 * refreshSubscriptionEntitlement() so the new tier unlocks immediately.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import { tierLabel, statusLabel } from '../../services/entitlementConsole';
import { refreshSubscriptionEntitlement } from '../../services/subscriptionEntitlement';
import {
  TIER_RANK_ORDER,
  UsdComparison,
  UsdEstimate,
  UsdMeter,
  UsdPlan,
  estimateTierMonthly,
  proratedUpgradePreview,
  usdBillingApi,
} from '../../services/usdBilling';

const money = (n: number | undefined | null): string => {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
};

export const TierBilling: React.FC = () => {
  const [plan, setPlan] = useState<UsdPlan | null>(null);
  const [meter, setMeter] = useState<UsdMeter | null>(null);
  const [estimate, setEstimate] = useState<UsdEstimate | null>(null);
  const [compare, setCompare] = useState<UsdComparison | null>(null);
  const [targetTier, setTargetTier] = useState<string | null>(null);
  const [estimatorUnits, setEstimatorUnits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeResult, setUpgradeResult] = useState<any | null>(null);

  const currentTierId = plan?.tier?.tierId || null;
  const currentTierName = plan?.tier?.name || (currentTierId ? tierLabel(currentTierId) : '—');

  const availableTargets = useMemo(() => {
    const idx = currentTierId ? TIER_RANK_ORDER.indexOf(currentTierId as any) : -1;
    if (idx < 0) return TIER_RANK_ORDER.filter((t) => t !== currentTierId) as unknown as string[];
    return TIER_RANK_ORDER.slice(idx + 1) as unknown as string[];
  }, [currentTierId]);

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, m, e] = await Promise.all([
        usdBillingApi.getPlan(),
        usdBillingApi.getMeter(),
        usdBillingApi.getEstimate(),
      ]);
      setPlan(p);
      setMeter(m);
      setEstimate(e);
      if (typeof m?.activeUnits === 'number') setEstimatorUnits(m.activeUnits);
    } catch (err: any) {
      setError(err?.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompare = useCallback(async (target: string) => {
    setComparing(true);
    try {
      const c = await usdBillingApi.getPlanCompare(target);
      setCompare(c);
    } catch (err: any) {
      setError(err?.message || 'Failed to load plan comparison');
    } finally {
      setComparing(false);
    }
  }, []);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (!targetTier) return;
    loadCompare(targetTier);
  }, [targetTier, loadCompare]);

  useEffect(() => {
    if (currentTierId && !targetTier && availableTargets.length > 0) {
      setTargetTier(availableTargets[0]);
    }
  }, [currentTierId, targetTier, availableTargets]);

  if (getAppMarket() !== 'texas') {
    return <div className="p-4 text-sm text-gray-500">Tier billing is only available in the Texas market.</div>;
  }

  const tiers = compare?.tiers || [];
  const activeUnits = meter?.activeUnits ?? 0;
  const currentTierConfig = tiers.find((t) => t.isCurrent) || tiers.find((t) => t.tierId === currentTierId) || null;
  const targetTierConfig = tiers.find((t) => t.isTarget) || tiers.find((t) => t.tierId === targetTier) || null;

  const currentMonthly = estimateTierMonthly(
    currentTierConfig?.baseFee ?? 0,
    currentTierConfig?.includedUnits ?? 0,
    currentTierConfig?.perUnitRate ?? 0,
    estimatorUnits,
  );
  const targetMonthly = estimateTierMonthly(
    targetTierConfig?.baseFee ?? 0,
    targetTierConfig?.includedUnits ?? 0,
    targetTierConfig?.perUnitRate ?? 0,
    estimatorUnits,
  );

  // T-134-001: server proration when it matches the estimator; a pure client
  // preview (mirroring billing-plan.js) when the operator changes unit count.
  const serverProration = compare?.proration?.ok ? compare.proration : null;
  const serverActiveUnits = serverProration?.activeUnits ?? activeUnits;
  const useServerProration = !!serverProration && estimatorUnits === serverActiveUnits;
  const preview =
    useServerProration || !serverProration || !currentTierConfig || !targetTierConfig
      ? null
      : proratedUpgradePreview({
          fromBaseFee: currentTierConfig.baseFee,
          toBaseFee: targetTierConfig.baseFee,
          fromIncludedUnits: currentTierConfig.includedUnits,
          toIncludedUnits: targetTierConfig.includedUnits,
          fromPerUnitRate: currentTierConfig.perUnitRate,
          toPerUnitRate: targetTierConfig.perUnitRate,
          activeUnits: estimatorUnits,
          remainingDays: serverProration.remainingDays ?? 0,
          cycleDays: serverProration.cycle?.days || 30,
        });

  const prorationView = (() => {
    if (useServerProration && serverProration) {
      return {
        dueNow: serverProration.dueNow ?? 0,
        days: `${serverProration.remainingDays ?? 0} day(s)`,
        fraction: serverProration.remainingFraction ?? 0,
        renewal: money(serverProration.nextRenewal?.amount),
        lineItems: serverProration.lineItems || [],
      };
    }
    if (preview) {
      return {
        dueNow: preview.dueNow,
        days: `${preview.remainingDays} day(s)`,
        fraction: preview.fraction,
        renewal: money(targetMonthly.total),
        lineItems: [],
      };
    }
    return null;
  })();

  const onUpgrade = async () => {
    if (!targetTier) return;
    setUpgrading(true);
    setError(null);
    setUpgradeResult(null);
    try {
      const res = await usdBillingApi.upgradePlan(targetTier);
      // T-134-003: unlock the new tier immediately via the entitlement service.
      await refreshSubscriptionEntitlement();
      setUpgradeResult(res);
      const next = TIER_RANK_ORDER[TIER_RANK_ORDER.indexOf(currentTierId as any) + 1];
      setTargetTier(next && next !== targetTier ? next : null);
      await loadCore();
    } catch (err: any) {
      setError(err?.message || 'Upgrade failed');
    } finally {
      setUpgrading(false);
    }
  };

  const billLines = estimate?.invoice?.lineItems || [];
  const subtotal = estimate?.invoice?.subtotal ?? 0;
  const overageLine = estimate?.invoice?.overage;
  const cycleKey = estimate?.invoice?.cycle?.cycleKey || meter?.cycle?.cycleKey || '—';

  return (
    <div data-testid="tier-billing" className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" data-testid="tier-billing-error">
          {error}
        </div>
      )}

      {/* current plan + meter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900" data-testid="tier-current-name">
              {currentTierName}
            </h2>
            <p className="text-sm text-gray-500">
              Status:{' '}
              <span className="font-medium text-gray-700" data-testid="tier-current-status">
                {statusLabel(plan?.subscriptionStatus)}
              </span>
              {plan?.pendingChange && (
                <span className="ml-2 text-amber-600" data-testid="tier-pending-change">
                  pending {plan.pendingChange.status}: {tierLabel(plan.pendingChange.toTier)}
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold text-slate-500 tracking-wider">METERED UNITS</div>
            <div className="text-3xl font-black text-gray-900" data-testid="tier-meter-units">
              {activeUnits}
            </div>
            <div className="text-xs text-gray-500">billing cycle {cycleKey}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs font-bold text-slate-500 tracking-wider">BASE FEE</div>
            <div className="text-xl font-bold text-gray-900" data-testid="tier-current-base-fee">
              {money(currentTierConfig?.baseFee)}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 tracking-wider">INCLUDED UNITS</div>
            <div className="text-xl font-bold text-gray-900" data-testid="tier-current-included-units">
              {currentTierConfig?.includedUnits ?? '—'}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 tracking-wider">UNIT RATE / UNIT</div>
            <div className="text-xl font-bold text-gray-900" data-testid="tier-current-unit-rate">
              {currentTierConfig ? money(currentTierConfig.perUnitRate) : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* T-133-001/002: tier table with unit-count display */}
      {tiers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-bold text-gray-900 mb-3" data-testid="tier-table-title">
            Plans at a glance
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" data-testid="tier-table">
              <thead>
                <tr className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-2 px-3">Plan</th>
                  <th className="py-2 px-3">Base fee</th>
                  <th className="py-2 px-3">Included units</th>
                  <th className="py-2 px-3">Per-unit rate</th>
                  <th className="py-2 px-3">Unit count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tiers.map((t) => (
                  <tr key={t.tierId} data-testid={`tier-row-${t.tierId}`}>
                    <td className="py-2 px-3 font-medium text-gray-900">
                      {t.name || tierLabel(t.tierId)}
                      {t.isCurrent && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-700" data-testid={`tier-base-${t.tierId}`}>
                      {money(t.baseFee)}
                    </td>
                    <td className="py-2 px-3 text-gray-700" data-testid={`tier-included-${t.tierId}`}>
                      {t.includedUnits}
                    </td>
                    <td className="py-2 px-3 text-gray-700" data-testid={`tier-rate-${t.tierId}`}>
                      {t.perUnitRate ? money(t.perUnitRate) : '—'}
                    </td>
                    <td className="py-2 px-3 text-gray-700" data-testid={`tier-units-${t.tierId}`}>
                      {t.includedUnits < activeUnits
                        ? `${activeUnits} units · ${activeUnits - t.includedUnits} over`
                        : `${activeUnits} / ${t.includedUnits} included`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* T-133-003: projected monthly bill */}
      {estimate?.invoice && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-bold text-gray-900 mb-3" data-testid="tier-bill-title">
            Projected monthly bill — {cycleKey}
          </h3>
          <div className="divide-y divide-gray-100">
            {billLines.map((line, i) => (
              <div
                key={`${line.kind}-${i}`}
                data-testid={`bill-line-${line.kind.toLowerCase()}`}
                className="flex justify-between py-2 text-sm"
              >
                <span className="text-gray-700">{line.description || line.kind}</span>
                <span className="font-medium text-gray-900">{money(line.amount)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between">
            <span className="font-bold text-gray-900">Total monthly bill</span>
            <span className="font-black text-gray-900" data-testid="bill-total">
              {money(subtotal)}
            </span>
          </div>
          {overageLine && overageLine.overageUnits > 0 && (
            <p className="mt-2 text-xs text-amber-600" data-testid="bill-overage">
              {overageLine.overageUnits} unit(s) over plan — {money(overageLine.amount)}
            </p>
          )}
        </div>
      )}

      {/* T-135-001: feature-limit comparison table */}
      {tiers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-bold text-gray-900 mb-3" data-testid="compare-title">
            Feature &amp; limit comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" data-testid="compare-table">
              <thead>
                <tr className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-2 px-3">Tier</th>
                  <th className="py-2 px-3">Base fee</th>
                  <th className="py-2 px-3">Included units</th>
                  <th className="py-2 px-3">Per-unit rate</th>
                  <th className="py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tiers.map((t) => (
                  <tr
                    key={t.tierId}
                    data-testid={`compare-row-${t.tierId}`}
                    className={t.isTarget ? 'bg-blue-50/50' : ''}
                  >
                    <td className="py-2 px-3 font-medium text-gray-900">
                      {t.name || tierLabel(t.tierId)}
                      {t.isTarget && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Target
                        </span>
                      )}
                      {t.isCurrent && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-700">{money(t.baseFee)}</td>
                    <td className="py-2 px-3 text-gray-700">{t.includedUnits}</td>
                    <td className="py-2 px-3 text-gray-700">{t.perUnitRate ? money(t.perUnitRate) : '—'}</td>
                    <td className="py-2 px-3 text-gray-700">{t.active === false ? 'Closed' : 'Available'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* T-134: mid-cycle upgrade */}
      {availableTargets.length > 0 && currentTierConfig && (
        <div
          className="bg-white rounded-xl border border-blue-200 shadow-sm p-5"
          data-testid="upgrade-card"
        >
          <h3 className="text-base font-bold text-gray-900 mb-1">Mid-cycle upgrade</h3>
          <p className="text-sm text-gray-500 mb-4">
            Upgrades take effect immediately and bill only the remainder of this cycle.
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {availableTargets.map((t) => (
              <button
                key={t}
                data-testid={`upgrade-target-${t}`}
                onClick={() => setTargetTier(t)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                  targetTier === t
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'
                }`}
              >
                {tierLabel(t)}
              </button>
            ))}
          </div>

          {comparing && <div className="text-sm text-gray-500 mb-4">Loading comparison…</div>}

          {compare && targetTierConfig && (
            <>
              {/* T-135-002: ROI summary */}
              {compare.roi && (
                <div
                  className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4"
                  data-testid="roi-summary"
                >
                  <div className="text-sm font-semibold text-emerald-900" data-testid="roi-headline">
                    {compare.roi.headline || 'ROI summary'}
                  </div>
                  <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-emerald-700">Annual delta: </span>
                      <span data-testid="roi-annual">{money(compare.roi.annualDelta)}</span>
                    </div>
                    <div>
                      <span className="text-emerald-700">Added included units: </span>
                      <span data-testid="roi-units">{compare.roi.addedIncludedUnits}</span>
                    </div>
                    <div>
                      <span className="text-emerald-700">Prorated today: </span>
                      <span data-testid="roi-today">{money(compare.roi.proratedChargeToday)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* T-135-002: overage cost estimator */}
              <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="estimator-units">
                  Overage cost estimator — units to bill
                </label>
                <input
                  id="estimator-units"
                  type="number"
                  min={0}
                  step={1}
                  value={estimatorUnits}
                  onChange={(e) => setEstimatorUnits(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
                  className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  data-testid="estimator-input"
                />
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-700">
                  <div>
                    <span className="text-slate-500">{tierLabel(currentTierId)} today: </span>
                    <span data-testid="estimator-current-total">{money(currentMonthly.total)}</span>
                    <span className="text-xs text-gray-400"> ({money(currentMonthly.overageCost)} overage)</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Target {tierLabel(targetTier)}: </span>
                    <span data-testid="estimator-target-total">{money(targetMonthly.total)}</span>
                    <span className="text-xs text-gray-400"> ({money(targetMonthly.overageCost)} overage)</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Delta: </span>
                    <span data-testid="estimator-delta">{money(targetMonthly.total - currentMonthly.total)}</span>
                  </div>
                </div>
              </div>

              {/* T-134-001: prorated mid-cycle math */}
              {prorationView ? (
                <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4" data-testid="upgrade-preview">
                  <div className="text-sm font-semibold text-blue-900 mb-2">Prorated mid-cycle charge</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-blue-700">Due now: </span>
                      <span className="font-bold" data-testid="upgrade-preview-due-now">
                        {money(prorationView.dueNow)}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700">Remaining: </span>
                      <span data-testid="upgrade-preview-days">{prorationView.days}</span>
                    </div>
                    <div>
                      <span className="text-blue-700">Fraction: </span>
                      <span data-testid="upgrade-preview-fraction">
                        {Math.round(prorationView.fraction * 100)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700">Next renewal: </span>
                      <span data-testid="upgrade-preview-renewal">{prorationView.renewal}</span>
                    </div>
                  </div>
                  {prorationView.lineItems.map((li, i) => (
                    <div key={`${li.kind}-${i}`} className="mt-2 flex justify-between text-sm text-blue-800">
                      <span>{li.description || li.kind}</span>
                      <span className="font-medium">{money(li.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                compare.prorationNote && (
                  <p className="mb-4 text-sm text-gray-500" data-testid="upgrade-preview-note">
                    {compare.prorationNote}
                  </p>
                )
              )}

              <button
                data-testid="upgrade-confirm"
                disabled={upgrading}
                onClick={onUpgrade}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50"
              >
                {upgrading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i> Upgrading…
                  </>
                ) : (
                  <>
                    <i className="fas fa-bolt mr-2"></i> Upgrade to {tierLabel(targetTier)}
                  </>
                )}
              </button>

              {upgradeResult && (
                <div
                  className="mt-4 rounded-lg bg-green-50 border border-green-200 p-4"
                  data-testid="upgrade-result"
                >
                  <div className="text-sm font-semibold text-green-900">
                    Upgrade to {tierLabel(upgradeResult?.toTier)} is live
                  </div>
                  <div className="mt-1 text-sm text-green-800">
                    Charged now: <span data-testid="upgrade-result-due-now">{money(upgradeResult?.proration?.dueNow)}</span>
                    {upgradeResult?.fastspring?.ok === false && (
                      <span className="ml-2 text-amber-700">(payment provider unreachable — retry from FastSpring account)</span>
                    )}
                  </div>
                  {upgradeResult?.nextRenewal && (
                    <div className="text-sm text-green-800">
                      Next renewal: {money(upgradeResult.nextRenewal.amount)} at {upgradeResult.nextRenewal.at}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {loading && <div className="text-sm text-gray-500"><i className="fas fa-spinner fa-spin mr-1"></i> Loading…</div>}
    </div>
  );
};

export default TierBilling;